import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import Sidebar from "../../components/Sidebar";
import Navbar from "../../components/Navbar";
import axiosInstance from "../../../axiosInstance";
import toast from "react-hot-toast";

const DEFAULT_FORM = {
  title: "",
  audience: "recruiter",
  price: "",
  durationMonths: "",
  durationLabel: "",
  description: "",
  features: "",
  badgeText: "",
  isBestValue: false,
  isActive: true,
  displayOrder: "",
};

const AUDIENCE_META = {
  recruiter: {
    title: "Recruiter Subscription Plans",
    icon: "🏢",
  },
  jobseeker: {
    title: "Job Seeker Plans",
    icon: "🧑",
  },
};

function AllPlansCard() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [form, setForm] = useState(DEFAULT_FORM);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/admin/subscription-plans");
      setPlans(response.data?.plans || []);
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to load plans");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const groupedPlans = useMemo(
    () => ({
      recruiter: plans.filter((plan) => plan.audience === "recruiter"),
      jobseeker: plans.filter((plan) => plan.audience === "jobseeker"),
    }),
    [plans]
  );

  const setField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setForm(DEFAULT_FORM);
    setEditingId("");
  };

  const handleEdit = (plan) => {
    setEditingId(plan._id);
    setForm({
      title: plan.title || "",
      audience: plan.audience || "recruiter",
      price: plan.price ?? "",
      durationMonths: plan.durationMonths ?? "",
      durationLabel: plan.durationLabel || "",
      description: plan.description || "",
      features: Array.isArray(plan.features) ? plan.features.join("\n") : "",
      badgeText: plan.badgeText || "",
      isBestValue: Boolean(plan.isBestValue),
      isActive: Boolean(plan.isActive),
      displayOrder: plan.displayOrder ?? "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Delete this plan?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
    });

    if (!result.isConfirmed) return;

    try {
      await axiosInstance.delete(`/admin/subscription-plans/${id}`);
      toast.success("Plan deleted successfully");
      fetchPlans();
      if (editingId === id) resetForm();
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to delete plan");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.title.trim()) {
      toast.error("Please enter plan title");
      return;
    }

    if (form.price === "") {
      toast.error("Please enter plan price");
      return;
    }

    try {
      setSaving(true);
      const payload = {
        ...form,
        price: Number(form.price),
        durationMonths: form.durationMonths === "" ? "" : Number(form.durationMonths),
        displayOrder: form.displayOrder === "" ? 0 : Number(form.displayOrder),
      };

      if (editingId) {
        await axiosInstance.put(`/admin/subscription-plans/${editingId}`, payload);
        toast.success("Plan updated successfully");
      } else {
        await axiosInstance.post("/admin/subscription-plans", payload);
        toast.success("Plan created successfully");
      }

      resetForm();
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to save plan");
    } finally {
      setSaving(false);
    }
  };

  const renderCard = (plan) => (
    <div className="col-12 col-lg-6 col-xl-4" key={plan._id}>
      <div className="card h-100 shadow-sm border-0">
        <div className="card-header border-gray-100 py-4 px-4">
          <div className="d-flex justify-content-between align-items-start gap-2 mb-3">
            <div>
              <div className="d-flex mb-2 align-items-end">
                <span className="h5 mb-0">₹</span>
                <span className="price display-5 mb-0 ms-1">{plan.price}</span>
                <span className="h6 fw-normal align-self-end ms-2">
                  /{plan.durationLabel || "Plan"}
                </span>
              </div>
              <h4 className="mb-2 text-black">
                {plan.title}{" "}
                {plan.isBestValue ? <span className="text-warning">⭐</span> : null}
              </h4>
              <p className="fw-normal mb-0">
                {plan.description || "No description added yet."}
              </p>
            </div>
            <span className={`badge ${plan.isActive ? "bg-success" : "bg-secondary"}`}>
              {plan.isActive ? "Active" : "Inactive"}
            </span>
          </div>
          {plan.badgeText ? (
            <span className="badge bg-warning text-dark">{plan.badgeText}</span>
          ) : null}
        </div>
        <div className="card-body py-4 px-4">
          {(Array.isArray(plan.features) ? plan.features : []).length ? (
            plan.features.map((feature, index) => (
              <div className="d-flex align-items-center mb-2" key={`${plan._id}-${index}`}>
                <span className="me-2">✓</span>
                <span>{feature}</span>
              </div>
            ))
          ) : (
            <div className="text-muted small">No features added.</div>
          )}
          <div className="small text-muted mt-3">
            Audience: {plan.audience === "recruiter" ? "Recruiter" : "Job Seeker"}
          </div>
          <div className="small text-muted">
            Order: {plan.displayOrder ?? 0}
          </div>
        </div>
        <div className="card-footer border-gray-100 d-flex gap-2 px-4 pb-4">
          <button
            type="button"
            className="btn btn-gray-800 w-100"
            onClick={() => handleEdit(plan)}
          >
            Edit
          </button>
          <button
            type="button"
            className="btn btn-outline-danger w-100"
            onClick={() => handleDelete(plan._id)}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Sidebar />
      <main className="content">
        <Navbar />

        <div className="py-4">
          <div className="d-flex justify-content-between w-100 flex-wrap mb-4">
            <div className="mb-3 mb-lg-0">
              <h1 className="h4">All Plans</h1>
              <p className="text-muted mb-0">
                Manage recruiter and job seeker subscription plans in one place.
              </p>
            </div>
          </div>

          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <h5 className="mb-0">{editingId ? "Edit Plan" : "Create Plan"}</h5>
                {editingId ? (
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={resetForm}
                  >
                    Cancel Edit
                  </button>
                ) : null}
              </div>

              <form className="row g-3" onSubmit={handleSubmit}>
                <div className="col-md-4">
                  <label className="form-label">Plan Title</label>
                  <input
                    className="form-control"
                    value={form.title}
                    onChange={(e) => setField("title", e.target.value)}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Audience</label>
                  <select
                    className="form-select"
                    value={form.audience}
                    onChange={(e) => setField("audience", e.target.value)}
                  >
                    <option value="recruiter">Recruiter</option>
                    <option value="jobseeker">Job Seeker</option>
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Price</label>
                  <input
                    type="number"
                    className="form-control"
                    value={form.price}
                    onChange={(e) => setField("price", e.target.value)}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Duration Label</label>
                  <input
                    className="form-control"
                    placeholder="1 Month / 3 Months / Forever"
                    value={form.durationLabel}
                    onChange={(e) => setField("durationLabel", e.target.value)}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Duration Months</label>
                  <input
                    type="number"
                    className="form-control"
                    placeholder="Leave blank for forever"
                    value={form.durationMonths}
                    onChange={(e) => setField("durationMonths", e.target.value)}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Display Order</label>
                  <input
                    type="number"
                    className="form-control"
                    value={form.displayOrder}
                    onChange={(e) => setField("displayOrder", e.target.value)}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Badge Text</label>
                  <input
                    className="form-control"
                    placeholder="Best Value / Signup Fee"
                    value={form.badgeText}
                    onChange={(e) => setField("badgeText", e.target.value)}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Description</label>
                  <input
                    className="form-control"
                    value={form.description}
                    onChange={(e) => setField("description", e.target.value)}
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">Features</label>
                  <textarea
                    rows="4"
                    className="form-control"
                    placeholder={"One feature per line"}
                    value={form.features}
                    onChange={(e) => setField("features", e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <div className="form-check mt-4 pt-2">
                    <input
                      id="isBestValue"
                      type="checkbox"
                      className="form-check-input"
                      checked={form.isBestValue}
                      onChange={(e) => setField("isBestValue", e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="isBestValue">
                      Best Value
                    </label>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="form-check mt-4 pt-2">
                    <input
                      id="isActive"
                      type="checkbox"
                      className="form-check-input"
                      checked={form.isActive}
                      onChange={(e) => setField("isActive", e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="isActive">
                      Active
                    </label>
                  </div>
                </div>
                <div className="col-md-6 d-flex align-items-end justify-content-end">
                  <button className="btn btn-primary" type="submit" disabled={saving}>
                    {saving ? "Saving..." : editingId ? "Update Plan" : "Create Plan"}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {loading ? (
            <div className="card border-0 shadow-sm">
              <div className="card-body text-muted">Loading plans...</div>
            </div>
          ) : (
            Object.entries(groupedPlans).map(([audience, items]) => (
              <div className="mb-5" key={audience}>
                <div className="d-flex align-items-center gap-2 mb-3">
                  <h4 className="mb-0">
                    {AUDIENCE_META[audience].icon} {AUDIENCE_META[audience].title}
                  </h4>
                </div>
                <div className="row g-4">
                  {items.length ? (
                    items.map(renderCard)
                  ) : (
                    <div className="col-12">
                      <div className="card border-0 shadow-sm">
                        <div className="card-body text-muted">
                          No {audience} plans found.
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </main>
    </>
  );
}

export default AllPlansCard;
